import React, { useEffect, useRef, MouseEvent, TouchEvent } from 'react';

// Add interface for Particle properties
interface ParticleProps {
  x: number;
  y: number;
  size: number;
  baseX: number;
  baseY: number;
  density: number;
  distance: number | null;
  velocity: {
    x: number;
    y: number;
  };
  color: string;
}

class Particle implements ParticleProps {
  x: number;
  y: number;
  size: number;
  baseX: number;
  baseY: number;
  density: number;
  distance: number | null;
  velocity: {
    x: number;
    y: number;
  };
  color: string;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.size = Math.random() * 2 + 1;
    this.baseX = x;
    this.baseY = y;
    this.density = (Math.random() * 30) + 1;
    this.distance = null;
    this.velocity = {
      x: Math.random() * 2 - 1,
      y: Math.random() * 2 - 1
    };
    this.color = 'rgba(150, 200, 255, 0.75)';
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
  }

  update(mouse: { x: number; y: number }, width: number, height: number) {
    const dx = mouse.x - this.x;
    const dy = mouse.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const forceDirectionX = dx / distance;
    const forceDirectionY = dy / distance;
    const maxDistance = 100;
    const force = (maxDistance - distance) / maxDistance;
    const directionX = forceDirectionX * force * this.density;
    const directionY = forceDirectionY * force * this.density;

    if (distance < maxDistance) {
      this.x -= directionX;
      this.y -= directionY;
      this.color = 'rgba(200, 225, 255, 0.9)';
    } else {
      this.color = 'rgba(150, 200, 255, 0.75)';
      
      if (this.x !== this.baseX) {
        const dx = this.x - this.baseX;
        this.x -= dx/20;
      }
      if (this.y !== this.baseY) {
        const dy = this.y - this.baseY;
        this.y -= dy/20;
      }
    }

    // Add slight movement
    this.x += this.velocity.x * 0.2;
    this.y += this.velocity.y * 0.2;

    // Bounce off edges
    if (this.x < 0 || this.x > width) this.velocity.x *= -1;
    if (this.y < 0 || this.y > height) this.velocity.y *= -1;
  }
}

// Add proper type annotations for the component
const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const frameRef = useRef<number | null>(null);

  const initParticles = (width: number, height: number) => {
    particlesRef.current = [];
    const numberOfParticles = (width * height) / 15000;
    
    for (let i = 0; i < numberOfParticles; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      particlesRef.current.push(new Particle(x, y));
    }
  };

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    
    if (!canvas || !ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particlesRef.current.forEach(particle => {
      particle.update(mouseRef.current, canvas.width, canvas.height);
      particle.draw(ctx);
    });

    connectParticles(ctx);
    frameRef.current = requestAnimationFrame(animate);
  };

  const connectParticles = (ctx: CanvasRenderingContext2D) => {
    for (let i = 0; i < particlesRef.current.length; i++) {
      for (let j = i + 1; j < particlesRef.current.length; j++) {
        const dx = particlesRef.current[i].x - particlesRef.current[j].x;
        const dy = particlesRef.current[i].y - particlesRef.current[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 120) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(150, 200, 255, ${0.3 * (1 - distance/120)})`;
          ctx.lineWidth = 1;
          ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
          ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
          ctx.stroke();
        }
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    contextRef.current = ctx;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles(canvas.width, canvas.height);
    };

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleTouchMove = (e: globalThis.TouchEvent) => {
      if (e.touches[0]) {
        mouseRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
      }
    };

    handleResize();
    animate();

    window.addEventListener('resize', handleResize as EventListener);
    window.addEventListener('mousemove', handleMouseMove as EventListener);
    window.addEventListener('touchmove', handleTouchMove as EventListener);

    return () => {
      window.removeEventListener('resize', handleResize as EventListener);
      window.removeEventListener('mousemove', handleMouseMove as EventListener); 
      window.removeEventListener('touchmove', handleTouchMove as EventListener);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
    />
  );
};

export default ParticleBackground;
